// Truncate and render an Algolia highlight value while keeping its <mark> spans intact.
// react-instantsearch hands components a hit whose matches are wrapped in <mark>/</mark>, so clamping
// a highlighted body means truncating against those tags -- counting only visible characters and
// never cutting inside a highlighted span -- then rendering the resulting parts.
const MARK_OPEN = "<mark>";
const MARK_CLOSE = "</mark>";

// The highlight value's text is HTML-escaped, so part text must be unescaped for display. These are
// the five entities react-instantsearch's escape produces.
const HTML_ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'" };
const ENTITY_PATTERN = /&(?:amp|lt|gt|quot|#39);/g;

export function unescapeHtml(text) {
  return text.replace(ENTITY_PATTERN, (entity) => HTML_ENTITIES[entity]);
}

// Splits a <mark>-tagged value into ordered { value, highlighted } parts with tags removed and text
// unescaped; empty runs between adjacent tags are dropped so no empty node renders.
export function highlightParts(value) {
  if (typeof value !== "string" || value === "") return [];

  const parts = [];
  let i = 0;
  let highlighted = false;

  while (i < value.length) {
    const tag = highlighted ? MARK_CLOSE : MARK_OPEN;
    const next = value.indexOf(tag, i);
    const end = next === -1 ? value.length : next;

    if (end > i) parts.push({ value: unescapeHtml(value.slice(i, end)), highlighted });
    if (next === -1) break;

    highlighted = !highlighted;
    i = next + tag.length;
  }

  return parts;
}

export function visibleLength(value) {
  return highlightParts(value).reduce((sum, part) => sum + part.value.length, 0);
}

// Returns the first `maxVisible` visible characters of the parts, dropping any part (or tail of a
// part) beyond the limit. A highlighted span that straddles the cut is kept up to the boundary and
// simply ends there -- no broken tags, since parts carry no tag characters.
export function truncateHighlightParts(value, maxVisible) {
  const out = [];
  let used = 0;

  highlightParts(value).forEach((part) => {
    const remaining = maxVisible - used;
    if (remaining <= 0) return;

    const kept = part.value.length <= remaining ? part.value : part.value.slice(0, remaining);
    out.push({ value: kept, highlighted: part.highlighted });
    used += kept.length;
  });

  return out;
}

// True when the value is truncated AND a highlighted span begins in the truncated-away tail (past
// `maxVisible` visible chars) -- a match the collapsed view would hide. A highlight that only
// straddles the boundary is not hidden, since truncateHighlightParts keeps it up to the cut.
export function hasHiddenHighlight(value, maxVisible) {
  let visibleSoFar = 0;

  return highlightParts(value).some((part) => {
    const startsInTail = visibleSoFar >= maxVisible;
    visibleSoFar += part.value.length;
    return part.highlighted && startsInTail;
  });
}
